import { Box, Divider, Stack, Typography } from "@mui/material";
import {
  format,
  formatDuration,
  intervalToDuration,
  millisecondsInSecond,
} from "date-fns";
import { useLayoutEffect, useState } from "react";
import { DoorIcon, PlayIcon } from "@/components/common/icons";
import DialogWithCloseButton from "@/components/common/modals/dialogWithCloseButton";
import { WorkShiftDialogType } from "@/components/serviceFlow/clockin/workShiftDialog/workShiftDialog";
import {
  GREY,
  SUCCESS_PRIMARY,
  TEXT_PRIMARY_DARK,
  TEXT_SECONDARY,
} from "@/config/mui/colorPalette";
import theme from "@/config/mui/theme";
import { useMedspaId } from "@/hooks/common/useMedspaId";
import useMedspaTimezone from "@/hooks/common/useMedspaTimezone";

export type EndWorkShiftDialogProps = {
  type: WorkShiftDialogType.END;
  isOpen: boolean;
  shiftStartDate: Date;
  breaksCumulativeDurationMinutes: number;
  handleEndShift: () => void;
  handleCancel: () => void;
};

export default function EndWorkShiftDialog({
  isOpen,
  shiftStartDate,
  breaksCumulativeDurationMinutes,
  handleEndShift,
  handleCancel,
}: EndWorkShiftDialogProps) {
  const medspaId = useMedspaId();
  const { utcToMedspaZonedTime } = useMedspaTimezone(medspaId);

  const [currentDate, setCurrentDate] = useState<Date | undefined>(undefined);

  useLayoutEffect(() => {
    setCurrentDate(utcToMedspaZonedTime(new Date().toISOString()));
    const updateShiftDurationInterval = setInterval(
      () => setCurrentDate(utcToMedspaZonedTime(new Date().toISOString())),
      millisecondsInSecond
    );

    return () => clearInterval(updateShiftDurationInterval);
  }, [utcToMedspaZonedTime]);

  if (!currentDate) return;

  const shiftDuration = intervalToDuration({
    start: shiftStartDate,
    end: currentDate,
  });
  const shiftDurationHoursMinutes = {
    hours: shiftDuration.hours,
    minutes: shiftDuration.minutes,
  };

  // edge case when shift last less than 1 minute and formatDuration returns an empty string
  const formattedDuration =
    formatDuration(shiftDurationHoursMinutes) || "0 minutes";

  return (
    <DialogWithCloseButton
      isOpen={isOpen}
      title="End your workshift"
      content={
        <>
          <Stack direction="row" sx={{ p: 2 }}>
            <Stack direction="row" sx={{ gap: 1, flexGrow: 1 }}>
              <PlayIcon size={24} color={GREY[60]} />
              <Stack sx={{ pt: "2px" }}>
                <Typography variant="paragraphSmall" color={TEXT_SECONDARY}>
                  Shift start time
                </Typography>
                <Typography variant="subtitleDefault">
                  {format(shiftStartDate, "p")}
                </Typography>
              </Stack>
            </Stack>
            <Divider
              orientation="vertical"
              variant="middle"
              flexItem
              sx={{ mx: 4 }}
            />
            <Stack direction="row" sx={{ gap: 1, flexGrow: 1 }}>
              <DoorIcon size={24} color={SUCCESS_PRIMARY} />
              <Stack sx={{ pt: "2px" }}>
                <Typography variant="paragraphSmall" color={TEXT_SECONDARY}>
                  Shift end time
                </Typography>
                <Typography variant="subtitleDefault">
                  {format(currentDate, "p")}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
          <Box
            sx={{
              width: "100%",
              py: 1,
              px: 1.5,
              backgroundColor: GREY[5],
              borderRadius: 2,
            }}
          >
            <Typography
              variant="paragraphSmall"
              color={TEXT_SECONDARY}
              sx={{ textAlign: "center" }}
            >
              You worked for{" "}
              <span
                style={{
                  ...theme.typography.labelSmall,
                  color: TEXT_PRIMARY_DARK,
                }}
              >
                {formattedDuration}
              </span>
              , including{" "}
              <span
                style={{
                  ...theme.typography.labelSmall,
                  color: TEXT_PRIMARY_DARK,
                }}
              >
                {breaksCumulativeDurationMinutes} minutes of breaks
              </span>
              .
            </Typography>
          </Box>
        </>
      }
      primaryButtonText="End shift"
      handlePrimaryClick={handleEndShift}
      handleCancel={handleCancel}
    />
  );
}
